import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'react-bootstrap';
import {
  FindSearchIndexesSourceEnum,
  UpdateAdverseMediaEntitiesListSourceEnum,
  UpdateSanctionListSourceEnum,
} from '../api';
import { adverseMediaEntityListApi, ApiSearchIndex, sanctionListApi } from '../apis';
import { showFeedback } from '../feedback/feedbackReducer';
import store from './../store/store';
import FormattedDateTime from '../common/datetime/FormattedDateTime';
import SalvPage from '../common/SalvPage';
import ScreeningCheckButton from './ScreeningCheckButton';
import { createColumnsForObjectData, SalvLabel, SalvButton, SalvTable, Size } from '@salvapp/ui-components';

const ScreeningLists = () => {
  const [indexes, setIndexes] = useState<ApiSearchIndex[] | undefined>(undefined);

  const updateScreeningList = async (list: UpdateSanctionListSourceEnum) => {
    try {
      await sanctionListApi.updateSanctionList({ source: list });
      store.dispatch(
        showFeedback({
          message: `List update started`,
          success: true,
        })
      );
    } catch (e) {
      store.dispatch(
        showFeedback({
          message: (e as Response).status === 409 ? 'A Dow Jones update is currently running' : 'Something went wrong',
          success: false,
        })
      );
    }
  };

  const updateAdverseMediaEntityList = async (list: UpdateAdverseMediaEntitiesListSourceEnum) => {
    try {
      await adverseMediaEntityListApi.updateAdverseMediaEntitiesList({
        source: list,
      });
      store.dispatch(
        showFeedback({
          message: `List update started`,
          success: true,
        })
      );
    } catch (e) {
      store.dispatch(
        showFeedback({
          message: (e as Response).status === 409 ? 'A Dow Jones update is currently running' : 'Something went wrong',
          success: false,
        })
      );
    }
  };

  const fetchActiveSearchIndexes = async (source: FindSearchIndexesSourceEnum) => {
    const activeSearchIndexes = await sanctionListApi.findSearchIndexes({
      source: source,
    });
    setIndexes(activeSearchIndexes);
  };

  useEffect(() => {
    if (!indexes) {
      fetchActiveSearchIndexes(FindSearchIndexesSourceEnum.DOWJONES);
    }
  }, [indexes]);

  const [isFullUpdateRunning, setFullUpdateRunning] = useState(false);
  const [isFullAMEUpdateRunning, setFullAMEUpdateRunning] = useState(false);

  const ameColumns = useMemo(
    () =>
      createColumnsForObjectData<{ listName: UpdateAdverseMediaEntitiesListSourceEnum }>([
        {
          accessorKey: 'listName',
          header: 'List',
          cell: (row) => row.listName,
        },
        {
          accessorKey: 'listName',
          header: 'Actions',
          cell: (row) => {
            return (
              <SalvButton
                onClick={() => {
                  setFullAMEUpdateRunning(true);
                  updateAdverseMediaEntityList(row.listName);
                }}
                size={Size.SMALL}
                disabled={isFullAMEUpdateRunning}
                label={'Update'}
              />
            );
          },
        },
      ]),
    []
  );

  const sColumns = useMemo(
    () =>
      createColumnsForObjectData<{ listName: UpdateSanctionListSourceEnum }>([
        {
          accessorKey: 'listName',
          header: 'List',
          cell: (row) => row.listName,
        },
        {
          accessorKey: 'listName',
          header: 'Actions',
          cell: (row) => {
            return (
              <SalvButton
                onClick={() => {
                  setFullUpdateRunning(true);
                  updateScreeningList(row.listName);
                }}
                size={Size.SMALL}
                disabled={isFullUpdateRunning}
                label='Update'
              />
            );
          },
        },
      ]),
    []
  );
  const sData = useMemo(() => Object.values(UpdateSanctionListSourceEnum).map((list) => ({ listName: list })), []);
  const ameData = useMemo(
    () => Object.values(UpdateAdverseMediaEntitiesListSourceEnum).map((list) => ({ listName: list })),
    []
  );

  return (
    <SalvPage title={'Screening Lists'}>
      <div className='row m-b-16'>
        <div className='col-6'>
          <SalvTable title='Sanctions' columns={sColumns} data={sData} />
        </div>
        <div className='col-6'>
          <SalvTable title='Adverse media entities' columns={ameColumns} data={ameData} />
        </div>
      </div>

      <SalvTable
        title='Active Indexes'
        className={'m-b-16'}
        columns={createColumnsForObjectData<ApiSearchIndex>([
          {
            accessorKey: 'name',
            header: 'Name',
            cell: (row) => row.name,
          },
          {
            accessorKey: 'type',
            header: 'Type',
            cell: (row) => <SalvLabel variant='secondary'>{row.type}</SalvLabel>,
          },
          {
            accessorKey: 'createdTime',
            header: 'Created At',
            cell: (row) => <FormattedDateTime date={row.createdTime} showTime />,
          },
        ])}
        data={indexes || []}
        emptyMessage={'Nothing to show here, yet.'}
      />
      <div className='row'>
        <div className='col-6'>
          <Card>
            <Card.Header>
              <span className='m-b-0 text-strong-m'>Person re-screening</span>
            </Card.Header>
            <Card.Body>
              In addition to Automatic Re-screening in every 24 hours, it is possible to trigger re-screening manually.
            </Card.Body>
            <Card.Footer className='text-end'>
              <ScreeningCheckButton />
            </Card.Footer>
          </Card>
        </div>
      </div>
    </SalvPage>
  );
};

export default ScreeningLists;
